import { Injectable } from '@angular/core';
import imageCompression from 'browser-image-compression';
import { ToastrService } from 'ngx-toastr';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';

import { FileModel } from '../../../../ui/my/routes/my-home/components/my-create-diary-modal/my-create-diary-modal.facade';
import { DiaryRepository } from '../../repositories/diary_repository';
import { DiaryEventProvider } from '../events/diary/diary_event_provider';
import { DiaryCreated, DiaryUpdated } from '../events/diary/diary_events';
import { Diary, DiaryProps } from './diary';
import { Picture } from './picture';

@Injectable()
export class DiaryCommands {
  constructor(
    private repository: DiaryRepository,
    private eventProvider: DiaryEventProvider,
    private toastr: ToastrService,
  ) {
    this.eventProvider.events$.subscribe((event) => {
      if (!event.notify) return;

      if (event instanceof DiaryCreated) {
        this.toastr.success('Journal créé');
      } else if (event instanceof DiaryUpdated) {
        this.toastr.success('Journal mis à jour');
      }
    });
  }

  async getPatientDiary(patientId: string, diaryId: string): Promise<Diary> {
    return this.repository.load(patientId, diaryId);
  }

  async getPatientDiaries(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Diary[]> {
    return this.repository.findDiaries(patientId, start, end);
  }

  async saveDiary(
    diaryProps: DiaryProps,
    diaryId: string | undefined = undefined,
    notify = true,
  ): Promise<Diary> {
    let diary = Diary.create(diaryProps, new UniqueEntityID(diaryId));
    if (diaryId !== undefined) {
      diary = await this.repository.save(diary);
      this.eventProvider.dispatch(new DiaryUpdated(diary, notify));
    } else {
      diary = await this.repository.create(diary);
      this.eventProvider.dispatch(new DiaryCreated(diary, notify));
    }
    return diary;
  }

  async generateDiaryPdf(
    patientId: string,
    date: Date,
    timeOffset: number,
    timeZone: string,
    withEncouragements: boolean,
    withImages: boolean,
  ) {
    return await this.repository.generatePdf(
      patientId,
      date,
      timeOffset,
      timeZone,
      withEncouragements,
      withImages,
    );
  }

  async newDiaryId(patientId: string): Promise<string> {
    return this.repository.newDiaryId(patientId);
  }

  async uploadFile(file: FileModel, folder: string): Promise<Picture> {
    const path = folder + '/' + file.file.name;
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    const compressedFile = await imageCompression(file.file, options);
    return this.repository.upload(path, compressedFile, file.type);
  }

  async removeFile(path: string) {
    return this.repository.removeFile(path);
  }

  async upsertDiary(diary: Diary, diaryId: string) {
    return this.repository.save(diary, diaryId);
  }

  async deleteDiary(diary: Diary) {
    return this.repository.delete(diary);
  }
}
